import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Clock, Filter } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const TYPE_LABELS = {
    case_due: "Due",
    case_chase: "Chase",
    case_created: "Opened",
    case_timeline: "Timeline",
    retention_review: "Retention",
};

const SEVERITY_STYLES = {
    urgent: "bg-rose-50 text-rose-700 border-rose-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-slate-50 text-slate-700 border-slate-200",
};

function dateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function addDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function monthRange(anchor) {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: dateKey(start), end: dateKey(end) };
}

function formatDate(value) {
    return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
    });
}

function eventTone(event) {
    return SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.info;
}

function EventCard({ event }) {
    const content = (
        <div className="border border-slate-200 bg-white rounded-lg p-3 hover:border-slate-300 transition-colors">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${eventTone(event)}`}>
                            {TYPE_LABELS[event.event_type] || event.event_type}
                        </span>
                        {event.category && (
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">{event.category}</span>
                        )}
                    </div>
                    <div className="font-heading text-sm font-semibold text-slate-900 mt-1">{event.title}</div>
                    {event.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{event.description}</p>}
                </div>
                {event.severity === "urgent" && <AlertTriangle size={16} className="text-rose-600 shrink-0 mt-1" />}
            </div>
            <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                {event.priority && <span>{event.priority} priority</span>}
                {event.status && <span>{event.status.replace("_", " ")}</span>}
                {event.visibility && <span>{event.visibility.replace("_", " ")}</span>}
            </div>
        </div>
    );
    return event.href ? <Link to={event.href}>{content}</Link> : content;
}

export default function EstateCalendar() {
    const [anchor, setAnchor] = useState(() => new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState("all");
    const [error, setError] = useState("");

    const range = useMemo(() => monthRange(anchor), [anchor]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/calendar", { params: range });
            setEvents(res.data || []);
            setError("");
        } catch (err) {
            setError(err?.response?.data?.detail || "Calendar could not be loaded");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.start, range.end]);

    const visibleEvents = useMemo(() => (
        typeFilter === "all" ? events : events.filter((event) => event.event_type === typeFilter)
    ), [events, typeFilter]);

    const grouped = useMemo(() => {
        return visibleEvents.reduce((acc, event) => {
            acc[event.date] = acc[event.date] || [];
            acc[event.date].push(event);
            return acc;
        }, {});
    }, [visibleEvents]);

    const monthDays = useMemo(() => {
        const first = new Date(`${range.start}T12:00:00`);
        const last = new Date(`${range.end}T12:00:00`);
        const days = [];
        for (let date = first; date <= last; date = addDays(date, 1)) {
            days.push(dateKey(date));
        }
        return days;
    }, [range]);

    const counts = {
        urgent: events.filter((event) => event.severity === "urgent").length,
        warning: events.filter((event) => event.severity === "warning").length,
        all: events.length,
    };

    const typeOptions = ["all", ...Object.keys(TYPE_LABELS)];
    const monthLabel = anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

    return (
        <div className="space-y-6" data-testid="estate-calendar-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                        <CalendarDays size={14} /> Estate calendar
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Deadlines and estate events</h1>
                    <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                        One place for case due dates, chase dates, retention reviews, case openings, and visible timeline activity.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))} data-testid="calendar-prev-month">
                        <ChevronLeft size={14} /> Previous
                    </Button>
                    <Button variant="outline" onClick={() => setAnchor(new Date())} data-testid="calendar-today">
                        Today
                    </Button>
                    <Button variant="outline" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))} data-testid="calendar-next-month">
                        Next <ChevronRight size={14} />
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-slate-200 bg-white rounded-lg p-4">
                    <div className="text-xs uppercase tracking-widest text-slate-500">All events</div>
                    <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{counts.all}</div>
                </div>
                <div className="border border-rose-200 bg-rose-50 rounded-lg p-4">
                    <div className="text-xs uppercase tracking-widest text-rose-700">Urgent</div>
                    <div className="font-heading text-2xl font-bold text-rose-800 mt-1">{counts.urgent}</div>
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                    <div className="text-xs uppercase tracking-widest text-amber-700">Warnings</div>
                    <div className="font-heading text-2xl font-bold text-amber-800 mt-1">{counts.warning}</div>
                </div>
            </div>

            <section className="border border-slate-200 bg-white rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <h2 className="font-heading text-lg font-semibold text-slate-900">{monthLabel}</h2>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        <Filter size={14} />
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                            data-testid="calendar-type-filter"
                        >
                            {typeOptions.map((type) => (
                                <option key={type} value={type}>{type === "all" ? "All event types" : TYPE_LABELS[type]}</option>
                            ))}
                        </select>
                    </label>
                </div>

                {error && <div className="text-sm text-rose-700 border border-rose-200 bg-rose-50 rounded-md p-3">{error}</div>}
                {loading && <div className="text-sm text-slate-500">Loading calendar...</div>}

                {!loading && !error && (
                    <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-5">
                        <div className="grid grid-cols-7 gap-1" data-testid="calendar-month-grid">
                            {monthDays.map((day) => {
                                const dayEvents = grouped[day] || [];
                                const isToday = day === dateKey(new Date());
                                const hasUrgent = dayEvents.some((event) => event.severity === "urgent");
                                return (
                                    <div
                                        key={day}
                                        className={`min-h-[92px] border rounded-md p-2 ${isToday ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"}`}
                                        data-testid={`calendar-day-${day}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs font-semibold text-slate-700">{Number(day.slice(-2))}</div>
                                            {dayEvents.length > 0 && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${hasUrgent ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                                                    {dayEvents.length}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 space-y-1">
                                            {dayEvents.slice(0, 3).map((event) => (
                                                <div key={event.id} className={`truncate text-[11px] border rounded px-1.5 py-0.5 ${eventTone(event)}`}>
                                                    {TYPE_LABELS[event.event_type] || event.event_type}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="space-y-4" data-testid="calendar-agenda">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                                <Clock size={13} /> Agenda
                            </div>
                            {Object.keys(grouped).length === 0 && (
                                <div className="text-sm text-slate-500 border border-slate-200 rounded-lg p-4">
                                    No calendar events match this month and filter.
                                </div>
                            )}
                            {Object.keys(grouped).sort().map((day) => (
                                <section key={day} className="space-y-2">
                                    <h3 className="font-heading text-sm font-semibold text-slate-900">{formatDate(day)}</h3>
                                    {(grouped[day] || []).map((event) => <EventCard key={event.id} event={event} />)}
                                </section>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
